// src/components/HeroCarousel.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface ArticleMeta {
  id: string;
  title: string;
  meta: string;
  heroImage: string;
  summary: string;
  category: string;
}

interface HeroCarouselProps {
  onOpenArticle: (id: string) => void;
}

const HeroCarousel: React.FC<HeroCarouselProps> = ({ onOpenArticle }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [articles, setArticles] = useState<ArticleMeta[]>([]);
  const navigate = useNavigate();

  // Only three articles as specified
  const featuredArticles: ArticleMeta[] = [
    {
      id: "importance-of-prayer",
      title: "The Importance of Prayer in Islam",
      meta: "7 min read",
      heroImage: "🕌",
      summary: "Discover why prayer is the central pillar of Islam and how it transforms your spiritual life",
      category: "Spiritual Growth"
    },
    {
      id: "power-of-gratitude", 
      title: "The Power of Gratitude in Islam",
      meta: "6 min read",
      heroImage: "🌅",
      summary: "How practicing Shukr can transform your life and bring you closer to Allah",
      category: "Spiritual Growth"
    },
    {
      id: "understanding-patience",
      title: "Understanding Patience in Islam", 
      meta: "8 min read",
      heroImage: "🏞️",
      summary: "The Islamic perspective on Sabr during difficult times and challenges",
      category: "Personal Development"
    }
  ];

  useEffect(() => {
    setArticles(featuredArticles);
  }, []);

  useEffect(() => {
    if (articles.length === 0) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % articles.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [articles.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % articles.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + articles.length) % articles.length);
  };

  const handleArticleClick = (articleId: string) => {
    // Navigate to articles library and open the specific article
    navigate('/articles');
    // Small delay to ensure navigation completes before opening modal
    setTimeout(() => {
      onOpenArticle(articleId);
    }, 100);
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') prevSlide();
      if (event.key === 'ArrowRight') nextSlide();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [articles.length]);

  if (articles.length === 0) return null;

  const currentArticle = articles[currentSlide];

  return (
    <section aria-label="Featured reads" style={{ width: '100%', maxWidth: '1000px', margin: '0 auto' }}>
      <div style={{ 
        position: 'relative', 
        borderRadius: '16px', 
        overflow: 'hidden', 
        backgroundColor: 'var(--surface)',
        boxShadow: '0 8px 32px var(--shadow)',
        height: '500px'
      }}>
        {/* Background with Nature Theme */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: `linear-gradient(135deg, 
            ${currentSlide === 0 ? '#667eea' : 
              currentSlide === 1 ? '#764ba2' : '#f093fb'} 0%, 
            ${currentSlide === 0 ? '#764ba2' : 
              currentSlide === 1 ? '#f093fb' : '#4facfe'} 100%)`,
          zIndex: 0
        }} />
        
        {/* Nature Icon */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          fontSize: '200px',
          opacity: '0.1',
          zIndex: 1
        }}>
          {currentArticle.heroImage}
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          aria-label="Previous slide"
          style={{
            position: 'absolute',
            left: '20px',
            top: '50%',
            transform: 'translateY(-50%)',
            backgroundColor: 'rgba(255,255,255,0.2)',
            border: 'none',
            borderRadius: '50%',
            width: '50px',
            height: '50px',
            cursor: 'pointer',
            zIndex: 3,
            color: 'white',
            fontSize: '20px',
            backdropFilter: 'blur(10px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.3)';
            e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
            e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
          }}
        >
          ←
        </button>
        
        <button
          onClick={nextSlide}
          aria-label="Next slide"
          style={{
            position: 'absolute',
            right: '20px',
            top: '50%',
            transform: 'translateY(-50%)',
            backgroundColor: 'rgba(255,255,255,0.2)',
            border: 'none',
            borderRadius: '50%',
            width: '50px',
            height: '50px',
            cursor: 'pointer',
            zIndex: 3,
            color: 'white',
            fontSize: '20px',
            backdropFilter: 'blur(10px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.3)';
            e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
            e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
          }}
        >
          →
        </button>

        {/* Content */}
        <button
          onClick={() => handleArticleClick(currentArticle.id)}
          aria-label={`Open article: ${currentArticle.title}`}
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            padding: '60px 40px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            zIndex: 2,
            color: 'white'
          }}
        >
          <div style={{ maxWidth: '800px', zIndex: 2, position: 'relative' }}>
            <span style={{
              display: 'inline-block',
              backgroundColor: 'rgba(255,255,255,0.9)',
              color: 'var(--text-primary)',
              padding: '8px 20px',
              borderRadius: '20px',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '24px',
              backdropFilter: 'blur(10px)'
            }}>
              {currentArticle.category}
            </span>
            
            <h2 style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 'clamp(2rem, 4vw, 3.5rem)',
              fontWeight: '700',
              lineHeight: '1.2',
              marginBottom: '20px',
              textShadow: '0 2px 4px rgba(0,0,0,0.5)'
            }}>
              {currentArticle.title}
            </h2>
            
            <p style={{
              fontSize: 'clamp(1.1rem, 2vw, 1.3rem)',
              opacity: '0.9',
              lineHeight: '1.6',
              marginBottom: '30px',
              maxWidth: '600px',
              marginLeft: 'auto',
              marginRight: 'auto',
              textShadow: '0 1px 2px rgba(0,0,0,0.5)'
            }}>
              {currentArticle.summary}
            </p>
            
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '16px',
              fontSize: '14px',
              opacity: '0.8'
            }}>
              <span>{currentArticle.meta}</span>
              <span style={{ fontSize: '8px' }}>●</span>
              <span>Featured Article</span>
            </div>
            
            <div style={{ marginTop: '32px' }}>
              <span style={{
                display: 'inline-block',
                backgroundColor: 'var(--accent-primary)',
                color: 'var(--bg-primary)',
                padding: '12px 32px',
                borderRadius: '25px',
                fontSize: '16px',
                fontWeight: '600',
                transition: 'all 0.3s ease'
              }}>
                Read Article
              </span>
            </div>
          </div>
        </button>

        {/* Pagination Dots */}
        <div style={{
          position: 'absolute',
          bottom: '30px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: '12px',
          zIndex: 3
        }}>
          {articles.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              aria-label={`Show slide ${index + 1}`}
              style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                backgroundColor: index === currentSlide ? 'var(--accent-primary)' : 'rgba(255,255,255,0.4)',
                transform: index === currentSlide ? 'scale(1.2)' : 'scale(1)'
              }}
            />
          ))}
        </div>

        {/* View All Articles Button */}
        <div style={{
          position: 'absolute',
          top: '30px',
          right: '30px',
          zIndex: 3
        }}>
          <button
            onClick={() => navigate('/articles')}
            style={{
              backgroundColor: 'rgba(255,255,255,0.9)',
              color: 'var(--text-primary)',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '20px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              backdropFilter: 'blur(10px)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255,255,255,1)';
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.9)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            View All Articles →
          </button>
        </div>
      </div>
    </section>
  );
};

export default HeroCarousel;